import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiClock, FiRefreshCw, FiSearch, FiSlash, FiTruck } from 'react-icons/fi';
import './BookingSection.css';
import CustomerReservationList from './modules/CustomerReservationList';
import { listReservations } from '../../../services/reservationsApi';

const IN_PROGRESS_STATUSES = [
  'in_progress',
  'in-progress',
  'en_curso',
  'en-curso',
  'intransit',
  'in_transit',
  'ongoing',
  'running',
  'live',
  'en_ruta',
];

const FINALIZED_STATUSES = [
  'finalized',
  'finalizada',
  'finalizado',
  'completed',
  'complete',
  'finished',
  'confirmed',
];
const REJECTED_STATUSES = ['rejected', 'declined'];
const CANCELLED_STATUSES = ['cancelled', 'canceled'];

const TAB_OPTIONS = [
  { key: 'all', label: 'Todas', statuses: null },
  { key: 'pending', label: 'Pendientes', statuses: ['pending'] },
  { key: 'accepted', label: 'Aceptadas', statuses: ['accepted'] },
  { key: 'inProgress', label: 'En curso', statuses: IN_PROGRESS_STATUSES },
  {
    key: 'finalized',
    label: 'Finalizadas',
    statuses: [...FINALIZED_STATUSES, ...REJECTED_STATUSES, ...CANCELLED_STATUSES],
  },
];

const STATUS_FILTERS = TAB_OPTIONS.reduce((acc, tab) => {
  acc[tab.key] = Array.isArray(tab.statuses)
    ? tab.statuses.map(status => (status || '').toString().toLowerCase())
    : null;
  return acc;
}, {});

const CLIENT_RESERVATIONS_KPI_STORAGE_KEY = 'flotte:clientReservationsKpiCollapsed';
const normalizeStatus = status => (status || '').toString().trim().toLowerCase();

const resolveTabFromStatus = status => {
  const normalized = normalizeStatus(status);
  if (!normalized) return 'all';
  if (normalized === 'pending') return 'pending';
  if (normalized === 'accepted') return 'accepted';
  if (IN_PROGRESS_STATUSES.includes(normalized)) return 'inProgress';
  if (
    FINALIZED_STATUSES.includes(normalized) ||
    REJECTED_STATUSES.includes(normalized) ||
    CANCELLED_STATUSES.includes(normalized)
  ) {
    return 'finalized';
  }
  return 'all';
};

export default function CustomerReservationsSection({ user }) {
  const userId = user?.id || '';
  const userEmail = user?.email || '';
  const userPhone = user?.phoneNumber || user?.phone || '';
  const location = useLocation();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [searchReservationId, setSearchReservationId] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('reservation') || '';
  });
  const [deepLinkReservationId, setDeepLinkReservationId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [kpiCollapsed, setKpiCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(CLIENT_RESERVATIONS_KPI_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const fetchReservations = useCallback(async () => {
    const hasIdentity = Boolean(userId || userEmail || userPhone);
    if (!hasIdentity) {
      setReservations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await listReservations({
        createdBy: userId,
        contactEmail: userEmail,
        contactPhone: userPhone,
        limit: 200,
      });
      setReservations(data);
    } catch (err) {
      setError(err?.message || 'No fue posible cargar tus reservas.');
    } finally {
      setLoading(false);
    }
  }, [userEmail, userId, userPhone]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        CLIENT_RESERVATIONS_KPI_STORAGE_KEY,
        kpiCollapsed ? 'true' : 'false'
      );
    } catch {
      // ignore
    }
  }, [kpiCollapsed]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextId = params.get('reservation') || '';
    setSearchReservationId(prev => (prev === nextId ? prev : nextId));
  }, [location.search]);

  const clearReservationQuery = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('reservation')) {
      return;
    }
    params.delete('reservation');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  const handleReservationPatched = useCallback(updatedRecord => {
    if (!updatedRecord?.id) return;
    setReservations(prev =>
      prev.map(item => (item.id === updatedRecord.id ? updatedRecord : item))
    );
  }, []);

  useEffect(() => {
    if (!searchReservationId) {
      return;
    }
    const match = reservations.find(item => item.id === searchReservationId);
    if (!match) {
      return;
    }
    const currentStatuses = STATUS_FILTERS[activeTab];
    const normalizedMatchStatus = normalizeStatus(match.status);
    const matchesCurrentTab =
      !Array.isArray(currentStatuses) || !currentStatuses.length
        ? true
        : currentStatuses.includes(normalizedMatchStatus);
    if (!matchesCurrentTab) {
      setActiveTab(resolveTabFromStatus(match.status));
    }
    setDeepLinkReservationId(match.id);
    setSearchReservationId('');
    clearReservationQuery();
  }, [activeTab, clearReservationQuery, reservations, searchReservationId]);

  const handleReservationFocusConsumed = useCallback(() => {
    setDeepLinkReservationId('');
  }, []);

  const numberFormatter = useMemo(() => new Intl.NumberFormat('es-MX'), []);

  const summaries = useMemo(() => {
    const base = {
      total: reservations.length,
      pending: 0,
      accepted: 0,
      inProgress: 0,
      rejected: 0,
      cancelled: 0,
      finalized: 0,
    };

    reservations.forEach(item => {
      const status = normalizeStatus(item.status);
      if (status === 'pending') {
        base.pending += 1;
        return;
      }
      if (status === 'accepted') {
        base.accepted += 1;
        return;
      }
      if (IN_PROGRESS_STATUSES.includes(status)) {
        base.inProgress += 1;
        return;
      }
      if (REJECTED_STATUSES.includes(status)) {
        base.rejected += 1;
        return;
      }
      if (CANCELLED_STATUSES.includes(status)) {
        base.cancelled += 1;
        return;
      }
      if (FINALIZED_STATUSES.includes(status)) {
        base.finalized += 1;
      }
    });

    base.all = base.total;
    base.finalizedAndCancelled = base.finalized + base.cancelled + base.rejected;
    return base;
  }, [reservations]);

  const filteredReservations = useMemo(() => {
    const targetStatuses = STATUS_FILTERS[activeTab];
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return reservations.filter(item => {
      const status = normalizeStatus(item.status);
      const matchesStatus =
        !Array.isArray(targetStatuses) || !targetStatuses.length
          ? true
          : targetStatuses.includes(status);

      if (!matchesStatus) return false;
      if (!normalizedSearch) return true;

      const haystack = [
        item.id,
        item.pickupLocation,
        item.dropoffLocation,
        item.publication?.title,
        item.unit?.model,
        item.unit?.tractorType,
      ]
        .map(value => (value || '').toString().toLowerCase())
        .join(' ');

      return haystack.includes(normalizedSearch);
    });
  }, [activeTab, reservations, searchTerm]);

  const hasSearchTerm = Boolean(searchTerm.trim());
  const emptyMessage = hasSearchTerm
    ? 'No encontramos reservas que coincidan con tu busqueda.'
    : 'No tienes reservas en este estado.';

  const kpiCards = useMemo(
    () => [
      {
        key: 'pending',
        label: 'Pendientes',
        value: numberFormatter.format(summaries.pending || 0),
        description: 'En espera de confirmacion',
        Icon: FiClock,
        tone: 'warning',
      },
      {
        key: 'accepted',
        label: 'Aceptadas',
        value: numberFormatter.format(summaries.accepted || 0),
        description: 'Listas para coordinar',
        Icon: FiCheckCircle,
        tone: 'success',
      },
      {
        key: 'inProgress',
        label: 'En curso',
        value: numberFormatter.format(summaries.inProgress || 0),
        description: 'Unidades en ruta',
        Icon: FiTruck,
        tone: 'info',
      },
      {
        key: 'finalized',
        label: 'Finalizadas / Canceladas',
        value: numberFormatter.format(summaries.finalizedAndCancelled || 0),
        description: 'Viajes cerrados o cancelados',
        Icon: FiSlash,
        tone: 'muted',
      },
    ], [
      numberFormatter,
      summaries.accepted,
      summaries.finalizedAndCancelled,
      summaries.inProgress,
      summaries.pending,
    ]
  );

  return (
    <div className="reservations-shell reservations-shell--client">
      <div className="client-reservations-container">
        <div className={`dashboard-kpi-wrapper${kpiCollapsed ? ' is-collapsed' : ''}`}>
          <div className="dashboard-kpi-wrapper__controls">
            <span className="dashboard-kpi-wrapper__label">Indicadores</span>
            <button
              type="button"
              className="dashboard-kpi-toggle"
              onClick={() => setKpiCollapsed(prev => !prev)}
              aria-pressed={!kpiCollapsed}
            >
              {kpiCollapsed ? 'Mostrar' : 'Ocultar'}
            </button>
          </div>
          {!kpiCollapsed ? (
            <section className="reservations-kpi-grid client-reservations-kpis" aria-label="Resumen de reservas">
              {kpiCards.map(card => (
                <article key={card.key} className="reservations-kpi-card">
                  <span className={`reservations-kpi-card__icon reservations-kpi-card__icon--${card.tone}`}>
                    <card.Icon aria-hidden="true" />
                  </span>
                  <div className="reservations-kpi-card__body">
                    <p className="reservations-kpi-card__label">{card.label}</p>
                    <strong className={`reservations-kpi-card__value reservations-kpi-card__value--${card.tone}`}>
                      {card.value}
                    </strong>
                    <span className="reservations-kpi-card__context">{card.description}</span>
                  </div>
                </article>
              ))}
            </section>
          ) : null}
        </div>

        <div className="reservations-table-card client-reservations-card">
          <div className="reservations-toolbar client-reservations-toolbar">
            <div className="reservations-toolbar__context">
              <label className="client-reservations-search">
                <FiSearch aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Buscar reserva por ID o ruta..."
                  value={searchTerm}
                  onChange={event => setSearchTerm(event.target.value)}
                  aria-label="Buscar reserva por ID o ruta"
                />
              </label>
            </div>

            <div className="reservations-toolbar__actions">
              <button
                type="button"
                className="reservations-toolbar__button"
                onClick={fetchReservations}
                disabled={loading || !userId}
              >
                <FiRefreshCw aria-hidden="true" />
                <span>{loading ? 'Sincronizando' : 'Actualizar'}</span>
              </button>
            </div>
          </div>

          <div className="reservations-table-card__body client-reservations-card__body">
            {error ? (
              <div className="client-reservations-alert" role="alert">
                {error}
              </div>
            ) : null}

            <CustomerReservationList
              items={filteredReservations}
              loading={loading}
              emptyMessage={emptyMessage}
              initialReservationId={deepLinkReservationId}
              onReservationFocus={handleReservationFocusConsumed}
              onReservationUpdate={handleReservationPatched}
              footerTitle="Mis reservas"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
